import { Button, Modal } from '@dev-spendesk/grapes';
import { useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { CountryFormField } from 'modules/suppliers/components/SupplierSpanishDprComplianceForm/CountryFormField';
import { SupplierFormField } from 'modules/suppliers/components/SupplierSpanishDprComplianceForm/SupplierFormField';
import { VatNumberFormField } from 'modules/suppliers/components/SupplierSpanishDprComplianceForm/VatNumberFormField';

import { useSupplierEditForm } from '../../hooks/useSupplierEditForm';
import { CreateSupplierQueryError } from '../SupplierBankInfoForm/CreateSupplierQueryError';
import { UpdateSupplierQueryError } from '../SupplierBankInfoForm/UpdateSupplierQueryError';

type Props = {
  supplier?: {
    id: string;
    name: string;
    country?: string;
    vatNumber?: string;
  };
  onSupplierSelected: (
    supplier: { id: string; name: string },
    confirmRightAfter?: boolean,
  ) => void;
  shouldDisplayDPRInformations?: boolean;
  shouldClearSpainClosureErrors?: boolean;
  isSupplierEditable?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  canCreateSupplier?: boolean;
};

export const SupplierEditForm = ({
  supplier,
  onSupplierSelected,
  onCancel,
  onConfirm,
  shouldDisplayDPRInformations,
  shouldClearSpainClosureErrors,
  isSupplierEditable,
  canCreateSupplier,
}: Props) => {
  const { t } = useTranslation('global');

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const selectedSupplier = supplier?.id !== 'unknown' ? supplier : undefined;

  const { formikProps, updateSupplierQueryState, createSupplierQueryState } =
    useSupplierEditForm({
      supplier: selectedSupplier,
      onSupplierSelected,
      shouldDisplayDPRInformations,
      shouldClearSpainClosureErrors,
      onSuccess: onConfirm,
    });

  const { setFieldValue, errors, values, submitForm } = formikProps;

  const allowEditDetails = supplier || values.newSupplierName?.length;

  const shouldConfirmBeforeSubmitting = Boolean(
    shouldDisplayDPRInformations &&
      selectedSupplier &&
      (values.vatNumber !== selectedSupplier.vatNumber ||
        values.country !== selectedSupplier.country),
  );

  return (
    <form className="flex flex-col gap-8">
      <SupplierFormField
        canCreateSupplier={canCreateSupplier}
        selectedSupplier={selectedSupplier}
        error={Boolean(errors.newSupplierName)}
        onChange={(change) => {
          setFieldValue(
            'newSupplierName',
            'newSupplierName' in change ? change.newSupplierName : undefined,
          );

          if ('existingSupplier' in change) {
            onSupplierSelected(change.existingSupplier);
          }
        }}
        showLabel={shouldDisplayDPRInformations}
        isDisabled={!isSupplierEditable}
      />

      {shouldDisplayDPRInformations && (
        <>
          <VatNumberFormField
            vatNumber={values.vatNumber ?? ''}
            error={errors.vatNumber}
            onChange={(vatNumber) => setFieldValue('vatNumber', vatNumber)}
            isDisabled={!allowEditDetails}
            showLabel
            showHelperText
            className=""
          />

          <CountryFormField
            country={values.country ?? ''}
            error={Boolean(errors.country)}
            onChange={(country) => setFieldValue('country', country)}
            isDisabled={!allowEditDetails}
            showLabel
            className=""
          />
        </>
      )}

      <div className="mt-8 flex gap-8">
        <Button
          data-testid="cancel-edit-supplier"
          text={t('misc.cancel')}
          variant="secondaryNeutral"
          fit="parent"
          onClick={onCancel}
          disabled={formikProps.isSubmitting}
        />

        <Button
          data-testid="submit-edit-supplier"
          text={t('misc.save')}
          variant="primaryBrand"
          fit="parent"
          onClick={() => {
            if (shouldConfirmBeforeSubmitting) {
              setIsConfirmModalOpen(true);
              return;
            }
            submitForm();
          }}
          disabled={formikProps.isSubmitting}
        />
      </div>

      <Modal
        isOpen={isConfirmModalOpen}
        iconName="circle-information"
        iconVariant="info"
        title={t('suppliers.bulkUpdateConfirmation.title')}
        onClose={() => setIsConfirmModalOpen(false)}
        actions={[
          <Button
            key="cancel"
            onClick={() => setIsConfirmModalOpen(false)}
            text={t('misc.cancel')}
            variant="secondaryNeutral"
            isDisabled={formikProps.isSubmitting}
          />,
          <Button
            key="confirm"
            text={t('suppliers.bulkUpdateConfirmation.confirm')}
            variant="primaryBrand"
            onClick={() => {
              setIsConfirmModalOpen(false);
              submitForm();
            }}
            isDisabled={formikProps.isSubmitting}
            isLoading={formikProps.isSubmitting}
          />,
        ]}
      >
        {shouldClearSpainClosureErrors ? (
          <ol className="mt-8 ml-16 list-outside list-decimal space-y-8 text-left">
            <li>
              {t('suppliers.bulkUpdateConfirmation.impact', {
                supplierName: selectedSupplier?.name ?? '',
              })}
            </li>
            <li>{t('suppliers.bulkUpdateConfirmation.removedHint')}</li>
          </ol>
        ) : (
          <p>
            {t('suppliers.bulkUpdateConfirmation.impact', {
              supplierName: selectedSupplier?.name ?? '',
            })}
          </p>
        )}
      </Modal>

      {createSupplierQueryState.status === 'error' && (
        <CreateSupplierQueryError
          createSupplierError={createSupplierQueryState.error}
        />
      )}
      {updateSupplierQueryState.status === 'error' && (
        <UpdateSupplierQueryError
          updateSupplierError={updateSupplierQueryState.error}
        />
      )}
    </form>
  );
};
